import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, type ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import PageLayout from '../components/PageLayout';

export default function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <PageLayout maxWidth="narrow">
        <div className="max-w-sm mx-auto">
          <Card>
            <svg
              className="oro-loading h-12 w-12 rounded-full bg-accent mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-text-secondary">Checking authentication...</p>
          </Card>
        </div>
      </PageLayout>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await authApi.requestMagicLink(email);
      setSuccess(true);
    } catch (err) {
      const apiError = err as ApiError;
      
      // Handle invite-required error - redirect to waitlist with email pre-populated
      if (apiError.code === 'INVITE_REQUIRED') {
        navigate(`/waitlist?email=${encodeURIComponent(email)}&source=blocked_signup`);
        return;
      }
      
      setError(apiError.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout maxWidth="narrow">
      <div className="max-w-sm mx-auto space-y-6">
        <div className="text-center space-y-2">
          <p className="text-sm uppercase tracking-[0.3em] text-text-muted">Access</p>
          <h1 className="text-h1 font-semibold">Login</h1>
          <p className="text-text-secondary">
            Enter your email to receive a secure magic link.
          </p>
        </div>
        <Card>
          {success && (
            <div className="mb-6 rounded-md border border-green-500/40 bg-green-500/10 p-4">
              <p className="text-sm text-green-100 text-center">
                Check your inbox! If an account exists, we sent a link.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 p-4">
              <p className="text-sm text-red-200 text-center">
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium tracking-wide text-text-secondary"
              >
                Email address
              </label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="you@example.com"
                required
                size="lg"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              loading={loading}
              className="w-full"
              size="lg"
            >
              Send Login Link
            </Button>
          </form>
        </Card>
      </div>
    </PageLayout>
  )
}
